import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import YouTubeIcon from "@mui/icons-material/YouTube";
import { extractYouTubeVideoId } from "@/helper/youTubePlayerUtils";
import { RootState } from "@/components/state/store";
import styles from "@/components/general/youTubeThumbnail.module.css";

interface YouTubeThumbnailProps {
  url: string;
  onClick: () => void;
  consentMessageClassName?: string;
  thumbnailClassName?: string;
  showTitle?: boolean;
  consentRequired?: boolean;
}

const YouTubeThumbnail: FC<YouTubeThumbnailProps> = ({
  url,
  onClick,
  consentMessageClassName = "",
  thumbnailClassName = "",
  showTitle = true,
  consentRequired = false,
}) => {
  const { t } = useTranslation();
  const hasConsented = useSelector((state: RootState) => state.youtubeConsent.hasConsented);

  const videoId = useMemo(() => {
    return extractYouTubeVideoId(url);
  }, [url]);

  if (!videoId) {
    return null;
  }

  if (consentRequired && !hasConsented) {
    return (
      <div
        className={`${styles.playButtonContainer} ${consentMessageClassName}`}
        onClick={onClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onClick();
          }
        }}
      >
        <YouTubeIcon className={styles.youtubeIcon} />
        {showTitle && (
          <span className={styles.playButtonText}>
            {t("expectation_from_examination_youtube_video")}
          </span>
        )}
      </div>
    );
  }

  return (
    <div
      className={`${styles.thumbnailContainer} ${thumbnailClassName}`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
    >
      <div className={styles.thumbnailWrapper}>
        <img
          src={`https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`}
          alt={t("youtube_video_thumbnail_alt")}
          className={styles.thumbnail}
          onError={(e) => {
            const target = e.target as HTMLImageElement;
            target.src = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
          }}
        />
        <div className={styles.playOverlay}>
          <svg
            className={styles.playIcon}
            viewBox="0 0 24 24"
            fill="currentColor"
          >
            <path d="M8 5v14l11-7z" />
          </svg>
        </div>
        <div className={styles.durationOverlay}>
          <svg
            className={styles.youtubeIconSvg}
            viewBox="0 0 24 24"
            fill="currentColor"
          >
            <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
          </svg>
        </div>
      </div>
      {showTitle && (
        <div className={styles.thumbnailTitle}>
          {t("expectation_from_examination_youtube_video")}
        </div>
      )}
    </div>
  );
};

export default YouTubeThumbnail;
